import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DataTable from "../../../../components/common/DataTable";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import ListExportToolbar from "../../../stock/components/list/ListExportToolbar";
import StatusBadge from "../../../../components/common/StatusBadge";
import {
  PETTY_CASH_VOUCHER_STATUS_CLASS,
  PETTY_CASH_VOUCHER_STATUS_LABEL,
  PETTY_CASH_RECEIPT_STATUS_CLASS,
  PETTY_CASH_RECEIPT_STATUS_LABEL,
  PETTY_CASH_RECEIPT_FILTERS,
} from "../../pettyCashStatusMaps";
import { useListPettyCashVouchersQuery } from "../../pettyCashApi";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";
import { useAllPages } from "../../useAllPages";
import { money, voucherNet as netOf, VOUCHER_REGISTER_EXPORT_COLUMNS } from "../voucherReport";

const voucherIdOf = (r) => r.recordGuid;

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "PAID", label: "Paid" },
  { value: "APPROVED", label: "Approved" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
];

// Itemized voucher listing for print/export. Every page of the range is
// loaded (useAllPages): it used to ask for 500 in one request, which the API
// turns into 50 (its per-request maximum is 200), so the register only ever
// held the first 50 vouchers (fixed 2026-09-15).
const VoucherRegisterReportPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("THIS_MONTH");
  const [status, setStatus] = useState("PAID");
  const [receiptStatus, setReceiptStatus] = useState("");

  const params = useMemo(
    () => ({
      branchId: selectedBranchId ?? undefined,
      status: status || undefined,
      receiptStatus: receiptStatus || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [selectedBranchId, status, receiptStatus, dateFrom, dateTo]
  );
  const { items: rows, totalCount, isLoading, isCapped, loaders } = useAllPages(useListPettyCashVouchersQuery, params, voucherIdOf);
  const isTruncated = isCapped;
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const totalRequested = rows.reduce((s, r) => s + Number(r.requestedAmount || 0), 0);
  const totalPaid = rows.reduce((s, r) => s + Number(r.paidAmount || 0), 0);
  // Returns (Batch168) - cash handed back against a paid voucher. Net = what
  // the voucher really cost the fund.
  const totalReturned = rows.reduce((s, r) => s + Number(r.returnedAmount || 0), 0);

  const columns = [
    {
      title: "Voucher",
      dataIndex: "voucherNumber",
      render: (v, r) => <Link to={paths.pettyCashVoucherDetail(r.recordGuid)}>{v}</Link>,
    },
    { title: "Date", dataIndex: "expenseDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Category", dataIndex: "categoryName", render: (v) => v || "-" },
    {
      // The theme sets `white-space: nowrap` on every table cell, so one long
      // description pushed the whole register off-screen. This column alone
      // wraps onto as many lines as it needs, inside a fixed width, so the
      // table fits the screen without a sideways scroll (2026-09-12).
      title: "Description",
      dataIndex: "description",
      width: 260,
      render: (v) => (
        <div style={{ whiteSpace: "normal", wordBreak: "break-word", minWidth: 180, maxWidth: 260, lineHeight: 1.35 }}>
          {v || "-"}
        </div>
      ),
    },
    { title: "Requested", dataIndex: "requestedAmount", render: (v) => money(v) },
    { title: "Paid", dataIndex: "paidAmount", render: (v) => (v == null ? "-" : money(v)) },
    {
      title: "Returned",
      dataIndex: "returnedAmount",
      render: (v) => (Number(v || 0) > 0 ? <span className="text-success">{money(v)}</span> : <span className="text-muted">&mdash;</span>),
    },
    { title: "Net", key: "net", render: (_, r) => (netOf(r) == null ? "-" : <strong>{money(netOf(r))}</strong>) },
    { title: "Paid To", dataIndex: "paidToEmployeeName", render: (v, r) => v || r.paidToPersonName || "-" },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => <StatusBadge status={v} classMap={PETTY_CASH_VOUCHER_STATUS_CLASS} labelMap={PETTY_CASH_VOUCHER_STATUS_LABEL} />,
    },
    {
      title: "Receipt",
      dataIndex: "receiptStatus",
      render: (v) =>
        v && v !== "NOT_REQUIRED" ? (
          <StatusBadge status={v} classMap={PETTY_CASH_RECEIPT_STATUS_CLASS} labelMap={PETTY_CASH_RECEIPT_STATUS_LABEL} />
        ) : (
          <span className="text-muted">&mdash;</span>
        ),
    },
  ];

  const exportColumns = VOUCHER_REGISTER_EXPORT_COLUMNS;

  return (
    <div className="page-wrapper">
      <div className="content">
        {loaders}
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Voucher Register</h4>
              <h6>Itemized voucher listing for print/export</h6>
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Vouchers</div>
              <div className="fs-5 fw-bold">{totalCount}</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Requested</div>
              <div className="fs-5 fw-bold">{money(totalRequested)}</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Paid</div>
              <div className="fs-5 fw-bold text-success">{money(totalPaid)}</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Returned</div>
              <div className="fs-5 fw-bold">{money(totalReturned)}</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Net Spend</div>
              <div className="fs-5 fw-bold">{money(totalPaid - totalReturned)}</div>
            </div></div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2 mb-2">
              <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <select
                  className="form-control form-control-sm"
                  style={{ width: 230 }}
                  value={receiptStatus}
                  onChange={(e) => setReceiptStatus(e.target.value)}
                >
                  {PETTY_CASH_RECEIPT_FILTERS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <select className="form-control form-control-sm" style={{ width: 180 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <ListExportToolbar
                  title="Petty Cash Voucher Register"
                  subtitleLines={[
                    `Branch: ${branchCode || "All"}`,
                    `Date: ${dateFrom} to ${dateTo}`,
                    `Status: ${status || "All"}`,
                    // Named on the printout so a filtered export can never be
                    // mistaken for the full register.
                    `Receipt: ${
                      (PETTY_CASH_RECEIPT_FILTERS.find((o) => o.value === receiptStatus) || {}).label || "Any"
                    }`,
                  ]}
                  branchCode={branchCode}
                  columns={exportColumns}
                  rows={rows}
                  fileName="PettyCashVoucherRegister"
                />
              </div>
            </div>
            {isTruncated && (
              <div className="alert alert-warning py-2 px-3 mb-2">
                Showing the first {rows.length} of {totalCount} matching vouchers - narrow the date range or filters for a complete register.
              </div>
            )}
            <div className="table-responsive">
              <DataTable columns={columns} dataSource={rows} rowKey={(r) => r.recordGuid} loading={isLoading} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherRegisterReportPage;
